import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { type FC, useEffect, useState } from 'react';
import { Loading } from '@/app/components/icons/Loading';
import { cardItemVariants, cardsContainerVariants } from '@/app/components/shared/motion-variants';
import type { CreditsPurchaseProduct } from '@/app/types';
import { cn } from '@/app/utils';
import CreditsItem from './components/CreditsItem';
import PurchaseCreditsFooter from './components/PurchaseCreditsFooter';

export const PurchaseCreditsContent: FC<{
  fromAgentProject?: boolean;
  source?: string;
  loadingProducts: boolean;
  error?: Error;
  products: CreditsPurchaseProduct[];
}> = ({ fromAgentProject, source, loadingProducts, error, products }) => {
  const t = useTranslations();

  const [currentProduct, setCurrentProduct] = useState<CreditsPurchaseProduct | null>(null);

  useEffect(() => {
    if (products) {
      if (currentProduct) {
        setCurrentProduct(products.find((v) => v.id === currentProduct?.id) || null);
        return;
      }
      setCurrentProduct(products[0]);
    }
  }, [products]);

  if (loadingProducts && !products?.length) {
    return (
      <div className="flex h-[440px] items-center justify-center">
        <Loading className="animate-spin text-3xl" />
      </div>
    );
  }

  if (!products?.length) {
    return (
      <div className="flex h-[440px] items-center justify-center">
        <div>{error ? t('payments.networkError') : t('popup.dialog.purchaseCredits.empty')}</div>
      </div>
    );
  }

  return (
    <>
      <div className={cn('flex flex-col justify-center text-sm', source === 'my-subscription' ? 'px-0 pt-6' : 'px-3')}>
        {source !== 'my-subscription' && !fromAgentProject && (
          <div className="px-2 py-1 sm:text-center">{t('popup.dialog.purchaseCredits.title')}</div>
        )}
        <motion.div
          className="grid grid-cols-3 gap-4 pb-4 sm:gap-2"
          initial="hidden"
          animate="visible"
          variants={cardsContainerVariants}
        >
          {products.map((product) => (
            <motion.div key={product.id} variants={cardItemVariants}>
              <CreditsItem
                product={product}
                active={product.id === currentProduct?.id}
                setCurrentProduct={setCurrentProduct}
              />
            </motion.div>
          ))}
        </motion.div>
        <div className="whitespace-pre-wrap text-system-white36">{t('popup.dialog.purchaseCredits.description')}</div>
      </div>
      <PurchaseCreditsFooter currentProduct={currentProduct} source={source} />
    </>
  );
};
